/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

// Component imports
import { Checkbox, Col, Divider, Form, Input, Row, Select, Space, Tooltip, Typography } from 'antd';
import { CopyOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import EntryPointSelect from '../Fields/EntryPointSelect';
import { GeneralFormStrategies } from './BotDeploymentsGeneralFormStrategy';
// Misc imports
import { BOT_DEPLOYMENTS_CHANNELS, LANGUAGE_OPTIONS } from '../../../constants/main';
import { useBotLanguage } from '../../../utils/hooks/useBotLanguage';
import userHasEditAccess from '../../../utils/misc/hasEditAccess';
import useTheme from '../../../utils/hooks/useTheme';
import BotDeploymentsDangerZoneForm from '../BotDeploymentsDangerZoneForm';
import { copyTextToClipboard } from '../../../utils/function/copyToClipboard';
import {
  FORM_CONFIGURATION_WEBCHAT_INTEGRATION_PATH,
  FORM_CONFIGURATION_WEBCHAT_LAYOUT_PATH,
  FORM_CONFIGURATION_VIBER_INTEGRATION_PATH,
  FORM_CONFIGURATION_MS_TEAMS_INTEGRATION_PATH,
  FORM_CONFIGURATION_FACEBOOK_INTEGRATION_PATH,
  FORM_CONFIGURATION_SLACK_INTEGRATION_PATH,
  FORM_CONFIGURATION_INSTAGRAM_INTEGRATION_PATH,
  FORM_CONFIGURATION_WHATSAPP_INTEGRATION_PATH
} from '../../../constants/deploymentFormPaths';
import { PLUGIN_CATEGORY } from '../../../constants/integrations';

const FORM_CONFIGURATION_LANGUAGE_PATH = ['settings', 'language'];

const msTeamsFieldTitles = ['clientId', 'clientSecret', 'azureTenantId', 'serviceUrl'];
const webchatFieldTitles = ['clientId', 'clientSecret', 'webSecretKey', 'webSecretKey', 'azureBotHandle'];

const BotDeploymentsGeneralForm = ({
  formData = {},
  userRoles = {},
  isCreateMode = false,
  onDeleteClick = () => {},
  form,
  description='',
  hasErrors=0,
  shouldMarkAsErroneous=()=>{},
  isLoadingMessengerProfile,     // We need this for facebook deployment
  fetchMessengerProfileError,    // We need this for facebook deployment
  applicationSettings,           // We need this for webchat deployment
  setApplicationSettings         // We need this for webchat deployment
}) => {
  const { t } = useTranslation();
  const { botLanguages } = useBotLanguage();
  const { formProps } = useTheme();
  const hasEditAccess = isCreateMode ? true : userHasEditAccess(formData?.tenant?.organizationId, userRoles?.organizationRoles, formData?.tenant.id, userRoles?.tenantRoles, true);
  const GeneralFormComponent = GeneralFormStrategies[formData.platform];

  const isDeploymentFromAutomatedAgentTestingPlugin = (deploymentId) => {
    return deploymentId === Object.values(formData?.tenant?.plugins || [])?.find(plugin => plugin.category === PLUGIN_CATEGORY.LLM_AUTOMATED_AGENT_TESTING)?.settings?.apiDeploymentId;
  };

  const onCopyIconClick = (textToCopy, label) => {
    copyTextToClipboard(textToCopy, t(`${label} copied to clipboard`));
  };

  useEffect(() => {
    if (!hasErrors) {
      shouldMarkAsErroneous(false);
      return;
    }
    const hasError = form.getFieldError(['displayName']).length > 0;

    let platformSpecificErrors = false;
    switch(formData.platform) {
      case BOT_DEPLOYMENTS_CHANNELS.MICROSOFT_WEB_CHAT.key:
        platformSpecificErrors = (webchatFieldTitles.some(fieldTitle =>
          form.getFieldError([...FORM_CONFIGURATION_WEBCHAT_INTEGRATION_PATH, fieldTitle]).length > 0
        ));
        break;
      case BOT_DEPLOYMENTS_CHANNELS.VIBER.key:
        platformSpecificErrors = form.getFieldError([...FORM_CONFIGURATION_VIBER_INTEGRATION_PATH, 'authToken']).length > 0;
        break;
      case BOT_DEPLOYMENTS_CHANNELS.WHATSAPP.key:
        if (
          form.getFieldError([...FORM_CONFIGURATION_WHATSAPP_INTEGRATION_PATH, 'accessToken']).length > 0
          || form.getFieldError([...FORM_CONFIGURATION_WHATSAPP_INTEGRATION_PATH, 'phoneNumberId']).length > 0
        ){
          platformSpecificErrors = true;
        }
        break;
      case BOT_DEPLOYMENTS_CHANNELS.MICROSOFT_TEAMS.key:
        platformSpecificErrors = (msTeamsFieldTitles.some(fieldTitle =>
          form.getFieldError([...FORM_CONFIGURATION_MS_TEAMS_INTEGRATION_PATH, fieldTitle]).length > 0
        ));
        break;
      case BOT_DEPLOYMENTS_CHANNELS.FACEBOOK.key: {
        if (
          form.getFieldError([...FORM_CONFIGURATION_FACEBOOK_INTEGRATION_PATH, 'pageAccessToken']).length > 0
          || form.getFieldError([...FORM_CONFIGURATION_FACEBOOK_INTEGRATION_PATH, 'pageId']).length > 0
        ){
          platformSpecificErrors = true;
          break;
        }
        const callToActions = form.getFieldValue('call_to_actions');
        const getStarted = form.getFieldValue('get_started');
        if (callToActions && callToActions.length > 0 && !getStarted) {
          platformSpecificErrors = true;
          break;
        }

        const hasError = callToActions?.some((actionObj, idx) => {
          if (
            form.getFieldError(['call_to_actions', idx, 'type']).length > 0
            || form.getFieldError(['call_to_actions', idx, 'title']).length > 0
            ||form.getFieldError(['call_to_actions', idx, 'data']).length > 0
            ||form.getFieldError(['call_to_actions', idx, 'url']).length > 0
          ) {
            return true;
          }
          return false;
        });

        platformSpecificErrors = hasError;
        break;
      }
      case BOT_DEPLOYMENTS_CHANNELS.SLACK.key:
        if(
          form.getFieldError([...FORM_CONFIGURATION_SLACK_INTEGRATION_PATH, 'appId']).length > 0
          || form.getFieldError([...FORM_CONFIGURATION_SLACK_INTEGRATION_PATH, 'clientId']).length > 0
          || form.getFieldError([...FORM_CONFIGURATION_SLACK_INTEGRATION_PATH, 'clientSecret']).length > 0
          || form.getFieldError([...FORM_CONFIGURATION_SLACK_INTEGRATION_PATH, 'teamId']).length > 0
          || form.getFieldError([...FORM_CONFIGURATION_SLACK_INTEGRATION_PATH, 'botUserOAuthToken']).length > 0
        ) {
          platformSpecificErrors = true;
        }
        break;
      case BOT_DEPLOYMENTS_CHANNELS.INSTAGRAM.key:
        if (
          form.getFieldError([...FORM_CONFIGURATION_INSTAGRAM_INTEGRATION_PATH, 'pageAccessToken']).length > 0
          || form.getFieldError([...FORM_CONFIGURATION_INSTAGRAM_INTEGRATION_PATH, 'fbConnectedPageId']).length > 0
          || form.getFieldError([...FORM_CONFIGURATION_INSTAGRAM_INTEGRATION_PATH, 'fbConnectedPageId']).length > 0
        ){
          platformSpecificErrors = true;
        }
        break;
      default:
        break;
    }

    shouldMarkAsErroneous(hasError || platformSpecificErrors);
  }, [ hasErrors, form]);

  return (
    <>
      <Row gutter={formProps.rowGutter} className={`${formProps.flexDirection} mb-6`}>
        <Col xs={formProps.mainColSpan}>
          <Typography.Text>
            {t('Deployment Details')}
          </Typography.Text>

          <Typography.Paragraph className="fs-sm" type="secondary">
            {description}
          </Typography.Paragraph>
        </Col>

        <Col xs={formProps.secondaryColSpan}>
          {
            !isCreateMode && (formData.platform === BOT_DEPLOYMENTS_CHANNELS.UNITY.key || formData.platform === BOT_DEPLOYMENTS_CHANNELS.API.key) ? (
              <Form.Item
                name="handle"
                label={t('Identifier')}
              >
                <Input
                  readOnly={true}
                  suffix={
                    <CopyOutlined
                      className="cursor-pointer"
                      onClick={() => onCopyIconClick(form.getFieldValue('handle'), 'Identifier')}
                    />
                  }
                />
              </Form.Item>
            ) : null
          }
          <Form.Item
            name="displayName"
            label={t('Name')}
            rules={[
              {
                required: true,
                message: t('Please input a deployment name')
              }
            ]}
          >
            {
              hasEditAccess
                ? <Input allowClear placeholder={t('Enter deployment name')}/>
                : <Typography.Text>{form.getFieldValue('displayName')}</Typography.Text>
            }
          </Form.Item>

          <Form.Item
            name="description"
            label={t('Description')}
          >
            {
              hasEditAccess
                ? <Input.TextArea
                  autoSize={{ minRows: 2 }}
                  showCount
                  maxLength={400}
                  allowClear
                  placeholder={t('Enter deployment description')}
                />
                : <Typography.Text>{form.getFieldValue('description')}</Typography.Text>
            }
          </Form.Item>

          <Form.Item
            label={t('Language')}
            rules={[
              {
                required: false,
                message: t('Please select a deployment language') }
            ]}
            name={[...FORM_CONFIGURATION_LANGUAGE_PATH]}
          >
            {
              hasEditAccess ? (
                <Select
                  placeholder={t('Choose a deployment language')}
                  allowClear="true"
                  showSearch
                  filterOption={(inputValue, option) => option.searchValue.toLowerCase()?.includes(inputValue.toLowerCase())}
                  options={botLanguages
                    .map(language => {
                      const currentLang = LANGUAGE_OPTIONS.find(lang => lang.key === language);
                      if(!currentLang) {
                        return {};
                      }
                      return {
                        label: <Space size={4}>{currentLang?.icon} {currentLang.label}</Space>,
                        value: currentLang.key,
                        searchValue: currentLang.label
                      };
                    })
                  }
                  getPopupContainer={triggerNode => triggerNode.parentElement}
                />
              ) : (
                <Typography.Text>{form.getFieldValue([...FORM_CONFIGURATION_LANGUAGE_PATH])}</Typography.Text>
              )
            }
          </Form.Item>

          <Form.Item
            name={['settings', 'automaticLanguageDetectionEnabled']}
            valuePropName="checked"
            className="mb-2 ml-1"
          >
            <Checkbox disabled={!hasEditAccess}>
              <Typography.Text>
                {t('Enable User Language Detection')}

                <Tooltip
                  className="ml-1"
                  placement="top"
                  title={t('Check this option if you want to enable user language detection.')}
                >
                  <QuestionCircleOutlined className="fs-sm position-absolute" />
                </Tooltip>
              </Typography.Text>
            </Checkbox>
          </Form.Item>

          {
            formData.platform === BOT_DEPLOYMENTS_CHANNELS.MICROSOFT_WEB_CHAT.key
              ? <Form.Item
                label={t('Flow')}
                name={[...FORM_CONFIGURATION_WEBCHAT_LAYOUT_PATH, 'entryPoint']}
              >
                {
                  hasEditAccess ? (
                    <EntryPointSelect
                      allowClear
                      placeholder={t('Select deployment starting flow')}
                    />
                  ) : <Typography.Text>{form.getFieldValue([...FORM_CONFIGURATION_WEBCHAT_LAYOUT_PATH, 'entryPoint'])}</Typography.Text>
                }
              </Form.Item>
              : null
          }

        </Col>
      </Row>
      {
        // renderPlatformSpecificFields()
        GeneralFormComponent
          ? <GeneralFormComponent formProps={formProps}
            hasEditAccess={hasEditAccess}
            form={form}
            isCreateMode={isCreateMode}
            applicationSettings={applicationSettings}
            setApplicationSettings={setApplicationSettings}
            isLoadingMessengerProfile={isLoadingMessengerProfile}
            fetchMessengerProfileError={fetchMessengerProfileError}
          />
          : null
      }

      {
        !hasEditAccess || isCreateMode || isDeploymentFromAutomatedAgentTestingPlugin(formData?.id)
          ? null
          : <div className="px-0 mb-4">
            <Divider />

            <BotDeploymentsDangerZoneForm
              formData={formData}
              userRoles={userRoles}
              isCreateMode={isCreateMode}
              onDeleteClick={onDeleteClick}
            />
          </div>
      }

    </>
  );
};

BotDeploymentsGeneralForm.propTypes = {
  formData: PropTypes.object,
  onDeleteClick: PropTypes.func,
  userRoles: PropTypes.object,
  form: PropTypes.object,
  description: PropTypes.string,
  hasErrors: PropTypes.number,
  shouldMarkAsErroneous: PropTypes.func,
  isLoadingMessengerProfile: PropTypes.bool,                           // We need this for facebook deployment
  fetchMessengerProfileError: PropTypes.bool,                          // We need this for facebook deployment
  isCreateMode: PropTypes.bool,
  applicationSettings: PropTypes.oneOf(['customApp', 'messageApp']),   // We need this for webchat deployment
  setApplicationSettings: PropTypes.func                               // We need this for webchat deployment
};

export default BotDeploymentsGeneralForm;
